import {
  LogAttributeType,
  Logging,
  LoggingAttribute
} from 'src/app/models/Logging';

/**
 * Type definition for a logging formatter function.
 *
 * @typedef {Function} LoggingFormatterFn
 * @param {string} value - The value to be formatted.
 * @param {LogAttributeType} type - The type of the log attribute.
 * @param {'new' | 'old'} mode - The mode indicating whether the value is new or old.
 * @param {LoggingAttribute} loggingAttribute - The logging attribute associated with the value.
 * @param {Logging} log - The logging object.
 * @returns {string} - The formatted string.
 */
export type LoggingFormatterFn = (
  value: string,
  type: LogAttributeType,
  mode: 'new' | 'old',
  loggingAttribute: LoggingAttribute,
  log: Logging
) => string;

/**
 * Type definition for a logging formatter map.
 *
 * @typedef {Map<string, LoggingFormatterFn>} LoggingFormatter
 */
export type LoggingFormatter = Map<string, LoggingFormatterFn>;

/**
 * Class to build a logging formatter.
 *
 * @template T - The type of the record.
 */
export class LoggingFormatterBuilder<T extends Record<string, unknown>> {
  /**
   * Static method to create a new instance of LoggingFormatterBuilder.
   *
   * @template O - The type of the record.
   * @returns {LoggingFormatterBuilder<O>} - A new instance of LoggingFormatterBuilder.
   */
  public static new<
    O extends Record<string, unknown>
  >(): LoggingFormatterBuilder<O> {
    return new LoggingFormatterBuilder<O>();
  }

  // Private constructor to prevent direct instantiation
  private constructor() {}

  // Private property to hold the formatter map
  private formatter: LoggingFormatter = new Map();

  /**
   * Adds a new formatter function to the map.
   *
   * @param {Extract<keyof T, string>} key - The key for the formatter function.
   * @param {LoggingFormatterFn} value - The formatter function.
   * @returns {LoggingFormatterBuilder<T>} - The current instance of LoggingFormatterBuilder.
   */
  public add(
    key: Extract<keyof T, string>,
    value: LoggingFormatterFn
  ): LoggingFormatterBuilder<T> {
    this.formatter.set(key, value);

    return this;
  }

  /**
   * Builds and returns the formatter map.
   *
   * @returns {LoggingFormatter} - The formatter map.
   */
  public build(): LoggingFormatter {
    return this.formatter;
  }
}

/**
 * Function to display the logging context.
 *
 * @param {string} _value - The value to be formatted (unused).
 * @param {LogAttributeType} _type - The type of the log attribute (unused).
 * @param {'new' | 'old'} mode - The mode indicating whether the value is new or old.
 * @param {LoggingAttribute} loggingAttribute - The logging attribute associated with the value.
 * @returns {string} - The logging context.
 */
export const displayLoggingContext: LoggingFormatterFn = (
  _value: string,
  _type: LogAttributeType,
  mode: 'new' | 'old',
  loggingAttribute: LoggingAttribute
): string => {
  if (mode === 'new') {
    return loggingAttribute.newContext ?? '';
  }

  return loggingAttribute.oldContext ?? '';
};
